export enum DeliveryTypeValues {
  OneOnOneClass = "One-on-one class",
  OneTimeClass = "One-time class",
  OngoingClass = "Ongoing class",
  SemesterCourse = "Semester course",
  ShortCourse = "Short course",
  Camp = "Camp",
  Club = "Group",
  Course = "Course",
  EnrichmentOrClub = "Enrichment or Club",
}

/** Learning format, used for tagging activities */
export enum FormatValues {
  /** Synchronous classes held in real-time with a teacher and students present */
  Live = "Live online",
  /** @deprecated replaced by SelfPaced. TODO: delete this */
  Flex = "Flexible schedule",
  /** Students progress through material at their own pace, no set schedule (aka "Async") */
  SelfPaced = "Self-Paced",
}

/** Preferences for learning format, used in learner profiles
 *
 * @see FormatValues for definitions
 * TODO: migrate learners and merge this with FormatValues
 */
export enum LearningFormatPreference {
  Live = "live",
  SelfPaced = "self_paced",
}

type IsOneOnOneFormatArgs = {
  size_max?: number | null;
  duration_weeks?: number | null;
  weekly_meetings?: number | null;
  hasTeacherSchedule?: boolean | null;
  uses_teacher_scheduling?: boolean | null;
};

export function isTutoring(activity?: IsOneOnOneFormatArgs) {
  if (isOneOnOneFormat(activity)) {
    return true;
  }
  if (activity!.size_max === 1) {
    return true;
  }
  return false;
}

export function isOneOnOneFormat(activity?: IsOneOnOneFormatArgs) {
  if (!activity) {
    return false;
  }
  if (!activity.hasTeacherSchedule && !activity.uses_teacher_scheduling) {
    return false;
  }
  if (activity.size_max !== 1) {
    return false;
  }
  return true;
}

type IsLiveOneTimeArgs = {
  is_self_paced?: boolean | null;
  is_ongoing_weekly?: boolean | null;
  weekly_meetings?: number | null;
  duration_weeks?: number | null;
};

export const isLiveOneTime = (activity: IsLiveOneTimeArgs) => {
  if (activity.is_ongoing_weekly) {
    return false;
  }
  if (activity.is_self_paced) {
    return false;
  }
  return activity.weekly_meetings === 1 && activity.duration_weeks === 1;
};

type IsOneTimeArgs = {
  is_self_paced?: boolean | null;
  is_ongoing_weekly?: boolean | null;
  weekly_meetings?: number | null;
  duration_weeks?: number | null;
};

export const isOneTime = (
  activity: IsOneTimeArgs,
  lessons: { uid: string }[],
) => {
  if (activity.is_ongoing_weekly) {
    return false;
  }
  if (activity.is_self_paced) {
    return lessons.length === 1;
  }
  return isLiveOneTime(activity);
};

export enum ClassFormatType {
  Course = "course",
  EnrichmentOrClub = "enrichment_or_club",
  OneOnOne = "one_on_one",
  OneTime = "one_time",
}

export type IsCourseArgs = IsOneTimeArgs & {
  is_club: boolean;
  is_ongoing_weekly: boolean | null;
  weekly_meetings: number | null;
  duration_weeks: number | null;
  subject: string | null;
  title: string | null;
  has_building_content: boolean;
  size_max: number | null;
};

export function isCourseExcludedSubject(activity: { subject: string | null }) {
  return (
    !activity.subject ||
    [
      "Arts",
      "Games & Hobbies",
      "Health & Wellness",
      "Life Skills",
      "Music",
      "Test Prep",
    ].includes(activity.subject)
  );
}

export function isCourse(
  activity: IsCourseArgs,
  lessons: { uid: string }[],
): boolean {
  const testPrepKeywords = [
    "test prep",
    "test-prep",
    "bootcamp",
    "boot-camp",
    "boot camp",
  ];
  const courseKeywords = [
    "full year",
    "full-year",
    "fullyear",
    "complete year",
    "complete-year",
    "completeyear",
    "full course",
    "full-course",
    "fullcourse",
    "curriculum",
    "semester",
  ];
  const tutorKeywords = ["tutor", "homework help", "coaching"];

  // Exclude chat groups
  if (activity.is_club) {
    return false;
  }
  // Exclude one time
  if (isOneTime(activity, lessons)) {
    return false;
  }
  // Exclude certain subjects
  if (!!activity.subject && isCourseExcludedSubject(activity)) {
    return false;
  }

  const title = activity.title;
  const weeklyMeetings: number = activity.weekly_meetings ?? 0;
  const durationWeeks: number = activity.duration_weeks ?? 0;
  // Check for test prep keywords in title
  if (
    !!title &&
    testPrepKeywords.some(keyword => title.toLowerCase().includes(keyword))
  ) {
    return false;
  }

  // Check for course keywords in title
  if (
    !!title &&
    courseKeywords.some(keyword => title.toLowerCase().includes(keyword))
  ) {
    return true;
  }

  // Conditions for ongoing classes with building content
  if (activity.is_ongoing_weekly && activity.has_building_content) {
    return true;
  }

  // Conditions for set end date classes that meet for 8+ weeks or meeting more than once a week
  if (
    !activity.is_ongoing_weekly &&
    (weeklyMeetings > 1 || durationWeeks >= 8)
  ) {
    // Exclude one-on-ones with tutor keywords
    if (
      activity.size_max === 1 &&
      tutorKeywords.some(
        keyword => !!title && title.toLowerCase().includes(keyword),
      )
    ) {
      return false;
    }
    return true;
  }

  return false;
}

export function getClassFormatType(
  activity: IsCourseArgs & IsOneTimeArgs & IsOneOnOneFormatArgs,
  lessons: { uid: string }[],
): ClassFormatType {
  if (activity.is_club) {
    return ClassFormatType.EnrichmentOrClub;
  }
  if (isOneTime(activity, lessons)) {
    return ClassFormatType.OneTime;
  }
  if (isCourse(activity, lessons)) {
    return ClassFormatType.Course;
  }
  if (isOneOnOneFormat(activity)) {
    return ClassFormatType.OneOnOne;
  }
  return ClassFormatType.EnrichmentOrClub;
}

type IsFixedLengthMultiDayLiveClassArgs = {
  weekly_meetings?: number | null;
  duration_weeks?: number | null;
  is_ongoing_weekly?: boolean | null;
  is_club?: boolean;
  isClub?: boolean;
  uses_teacher_scheduling?: boolean;
  hasTeacherSchedule?: boolean;
};

export function isFixedLengthMultiDayLiveClass(
  activity: IsFixedLengthMultiDayLiveClassArgs,
): boolean {
  if (
    !activity ||
    !activity.duration_weeks ||
    !activity.weekly_meetings ||
    activity.is_ongoing_weekly ||
    Boolean(activity?.hasTeacherSchedule) ||
    Boolean(activity?.uses_teacher_scheduling) ||
    Boolean(activity?.isClub) ||
    Boolean(activity?.is_club)
  ) {
    return false;
  }

  if (activity.duration_weeks === 1 && activity.weekly_meetings > 1) {
    return true;
  }

  if (activity.duration_weeks > 1 && activity.weekly_meetings >= 1) {
    return true;
  }

  return false;
}

type IsOngoingGroupLiveClassArgs = IsFixedLengthMultiDayLiveClassArgs & {
  isSelfPaced?: boolean | null;
  is_self_paced?: boolean | null;
};

export function isOngoingGroupLiveClass(
  activity: IsOngoingGroupLiveClassArgs | null | undefined,
): boolean {
  return Boolean(
    activity &&
      activity.is_ongoing_weekly === true &&
      activity.isSelfPaced !== true &&
      activity.is_self_paced !== true &&
      !activity.isClub &&
      !activity.is_club &&
      !activity.hasTeacherSchedule &&
      !activity.uses_teacher_scheduling,
  );
}

/** Live formats that can use the V2 classroom template. */
export function isLiveClassroomV2Format(
  activity: IsOngoingGroupLiveClassArgs | null | undefined,
): boolean {
  return (
    !!activity &&
    (isFixedLengthMultiDayLiveClass(activity) ||
      isOngoingGroupLiveClass(activity))
  );
}

type GetScheduleClassFormatTypeArgs = IsFixedLengthMultiDayLiveClassArgs &
  IsLiveOneTimeArgs &
  IsOneOnOneFormatArgs;

/**
 * Classifies by schedule (fixed length vs recurring weekly), unlike
 * getClassFormatType which classifies by curriculum depth (Course requires
 * 8+ weeks, course keywords, etc.). Returns undefined when the schedule is
 * incomplete so callers can fall back to the stored class format.
 */
export function getScheduleClassFormatType(
  activity: GetScheduleClassFormatTypeArgs,
): ClassFormatType | undefined {
  if (activity.is_club || activity.isClub) {
    return ClassFormatType.EnrichmentOrClub;
  }
  if (isOneOnOneFormat(activity)) {
    return ClassFormatType.OneOnOne;
  }
  if (activity.is_ongoing_weekly) {
    return ClassFormatType.EnrichmentOrClub;
  }
  if (isLiveOneTime(activity)) {
    return ClassFormatType.OneTime;
  }
  if (isFixedLengthMultiDayLiveClass(activity)) {
    return ClassFormatType.Course;
  }
  return undefined;
}

export const SELF_PACED_MAX_DELAY_START_TIME = 180;

export const ageOptions = [
  3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18,
];
export const classAgeMin = ageOptions[0];
export const ageMax = ageOptions[ageOptions.length - 1];

export const toddlerClassAgeOptions = [1, 2];
export const toddlerClassAgeMin = toddlerClassAgeOptions[0];
export const toddlerClassAgeMax =
  toddlerClassAgeOptions[toddlerClassAgeOptions.length - 1];

export const clubAgeOptions = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
export const clubAgeMin = clubAgeOptions[0];

export enum USGradeLevel {
  Preschool = "Preschool",
  PreKindergarten = "Pre-Kindergarten",
  Kindergarten = "Kindergarten",
  FirstGrade = "1st grade",
  SecondGrade = "2nd grade",
  ThirdGrade = "3rd grade",
  FourthGrade = "4th grade",
  FifthGrade = "5th grade",
  SixthGrade = "6th grade",
  SeventhGrade = "7th grade",
  EighthGrade = "8th grade",
  NinthGrade = "9th grade",
  TenthGrade = "10th grade",
  EleventhGrade = "11th grade",
  TwelfthGrade = "12th grade",
}

export const usGradeLevels = Object.values(USGradeLevel) as USGradeLevel[];

// toddler classes are offered for ages 1-2 and marketed as "Together Time" in the product
export function isToddlerClass(activity?: { age_min?: number | null }) {
  if (activity?.age_min === 1) {
    return true;
  }
  return false;
}

export function isToddlerClassSupported(activity?: {
  isClub: boolean;
  isSelfPaced: boolean;
  hasTeacherSchedule: boolean;
}) {
  if (
    activity?.isClub ||
    activity?.isSelfPaced ||
    activity?.hasTeacherSchedule
  ) {
    return false;
  }
  return true;
}
